import { format } from 'date-fns';
import moment from 'moment';
import { ValidationError } from 'yup';
import { Palette } from '@mui/material';

import { Order } from '@zendesk/forethought-elements';
import { DateRange } from '../discover-dashboard-page/types';
import { InsightTopicData } from './topics/types';
import {
  EMAIL_NO_RESPONSE_LABEL_MAPPER,
  INSIGHTS_DATE_FORMAT,
} from './constants';
import {
  ChatTrendsPeriodicalFilter,
  ColorMapConfig,
  InsightSortDirection,
} from './types';
import { NA } from 'src/pages/intent-conversation-analytics/constants';
import { IntentData } from 'src/types/workflowBuilderAPITypes';
import {
  toInsightsDayEndSeconds,
  toInsightsDayStartSeconds,
} from 'src/utils/insightsTime';
import { uuidOrEmptySchema } from 'src/utils/validators';

export const stringifyDateRange = (range: DateRange) => {
  let dateRangeString = '';

  if (range?.from) {
    dateRangeString += format(range.from, INSIGHTS_DATE_FORMAT);
  }

  if (range?.to) {
    dateRangeString += ` - ${format(range.to, INSIGHTS_DATE_FORMAT)}`;
  }

  return dateRangeString;
};

// The range Dates' calendar fields identify the intended UTC days; re-anchor
// to UTC instants so every viewer sends identical epochs (FTSV-1332).
export const dateRangeToTimestamp = (dateRange: DateRange) => {
  const now = Date.now();
  const startTime = dateRange.from
    ? toInsightsDayStartSeconds(dateRange.from)
    : Math.floor(now / 1000 - 30 * 24 * 60 * 60);
  const endTime = dateRange.to
    ? toInsightsDayEndSeconds(dateRange.to)
    : Math.floor(now / 1000);
  return {
    end_timestamp: endTime,
    start_timestamp: startTime,
  };
};

export const insightSortDirectionToSortDirection = (
  direction: InsightSortDirection,
): Order => (direction === 'ASC' ? 'asc' : 'desc');

export const sortDirectionToInsightSortDirection = (
  direction: Order,
): InsightSortDirection => (direction === 'asc' ? 'ASC' : 'DESC');

export const numbersToStringWithCommas = ({
  canBeFloat,
  maximumFractionDigits,
  minimumFractionDigits,
  number,
  showDecimals = false,
  style = 'decimal',
}: {
  canBeFloat?: boolean;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  number?: number;
  showDecimals?: boolean;
  style?: 'decimal' | 'percent';
}) => {
  const getMaxFractionDigits = () => {
    // Used for the custom formats
    if (maximumFractionDigits) {
      return maximumFractionDigits;
    }
    if (showDecimals) {
      return 3;
    }
    const showFixedPoints =
      canBeFloat && style === 'decimal' && !Number.isInteger(number);
    if (showFixedPoints) {
      return 2;
    }
    return 0;
  };
  return (
    number?.toLocaleString(undefined, {
      maximumFractionDigits: getMaxFractionDigits(),
      minimumFractionDigits: minimumFractionDigits,
      style,
    }) ?? NA
  );
};

export const percentageValueAsString = (value: number) => {
  // 0.25 => +25%, -0.25 => -25%
  return `${value >= 0 ? '+' : ''}${numbersToStringWithCommas({
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    number: value,
    style: 'percent',
  })}`;
};

export const formatNumbersWithComma = (value: number) => {
  return value.toLocaleString('en-US');
};

export const numberToCurrency = ({
  includeDigits = false,
  number,
  shouldRound = false,
}: {
  includeDigits?: boolean;
  number?: number;
  shouldRound?: boolean;
}) => {
  if (includeDigits) {
    return (number || 0).toLocaleString('en-US', {
      currency: 'USD',
      style: 'currency',
    });
  }

  return (number || 0).toLocaleString('en-US', {
    currency: 'USD',
    maximumFractionDigits: shouldRound ? 0 : undefined,
    minimumFractionDigits: 0,
    style: 'currency',
  });
};

export const getTooltipForMetric = (
  label: string,
  costPerTicket: number | null = null,
): string => {
  switch (label) {
    case 'realized_savings':
      if (costPerTicket) {
        return `Dollarized savings assuming a $${costPerTicket} cost per deflection.`;
      }
    case 'potential_savings':
      return 'Maximum potential savings you could have realized for this topic with Discover automation.';
    default:
      return '';
  }
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const periodToTimestamps = (
  period: ChatTrendsPeriodicalFilter,
  index: number,
) => {
  const periodMapper: {
    [Property in ChatTrendsPeriodicalFilter]: 'months' | 'quarters' | 'weeks';
  } = {
    monthly: 'months',
    quarterly: 'quarters',
    weekly: 'weeks',
  };
  const periodValue = periodMapper[period];
  const periodValueSingular = periodValue.substring(
    0,
    periodValue.length - 1,
  ) as 'month' | 'quarter' | 'week';
  const start = moment()
    .subtract(index, periodValue)
    .startOf(periodValueSingular)
    .toDate();
  const end = moment()
    .subtract(index, periodValue)
    .endOf(periodValueSingular)
    .toDate();
  // dateRangeToTimestamp reads only the calendar fields; time-of-day is ignored
  const result = dateRangeToTimestamp({ from: start, to: end });
  return [result.start_timestamp, result.end_timestamp];
};

export const periodToTimestampsByNumOfRange = ({
  numberOfRanges = 4,
  period,
}: {
  numberOfRanges?: number;
  period: ChatTrendsPeriodicalFilter;
}) => {
  /**
   * Get timestamps based on period
   * The timestamp will be be in this format [timestamp_1, timestamp_2, ... timestamp_8]
   * Where timestamp_1 and timestamp_2 would be the oldest range (start, end)
   * and continue to get the latest (oldest range -> recent range)
   */
  if (period === 'weekly') {
    const timestamps = [...Array(numberOfRanges)].map((_, i) => {
      // First day starts on Sunday, ends at Saturday
      return periodToTimestamps(period, i);
    });
    return timestamps.reverse().flat();
  }
  if (period === 'monthly') {
    const timestamps = [...Array(numberOfRanges)].map((_, i) => {
      return periodToTimestamps(period, i);
    });
    return timestamps.reverse().flat();
  }
  if (period === 'quarterly') {
    const timestamps = [...Array(numberOfRanges)].map((_, i) => {
      return periodToTimestamps(period, i);
    });
    return timestamps.reverse().flat();
  }
  return [];
};

export const isUiQueryError = ({
  searchQueryMode,
  uiQuery,
}: {
  searchQueryMode: string;
  uiQuery: string;
}) => {
  if (searchQueryMode !== 'conversation_id') {
    return '';
  }

  try {
    uuidOrEmptySchema.validateSync(uiQuery);

    return '';
  } catch (err) {
    return err instanceof ValidationError
      ? err.message
      : 'An unexpected error occurred';
  }
};

export function getIntentFilterOptions(intents: IntentData[]) {
  const intentOptions = intents.map(intent => ({
    label: intent.intent_name,
    value: `intent_id.${intent.intent_definition_id}`,
  }));

  return intentOptions;
}

export const getReadableNoResponseString = (reason: string) => {
  // If the reason is not found in the map, use sentence case on existing value (for Email)
  // Scenario 1 - (Exist in mapper) - user_rate_limited_exceeded -> Email response limit exceeded
  // Scenario 2 - (Not exist in mapper) - out_of_office_reply_foo_bar -> Out of office reply foo bar
  const readableReason =
    EMAIL_NO_RESPONSE_LABEL_MAPPER[
      reason as keyof typeof EMAIL_NO_RESPONSE_LABEL_MAPPER
    ] ??
    reason
      .split('_')
      .join(' ')
      .toLowerCase()
      // Capitalizes only the first letter of the string to achieve sentence case.
      .replace(/^\w/, c => c.toUpperCase());
  return readableReason;
};

export const getKeyValueFromFilterQuery = ({
  filterString,
}: {
  filterString: string;
}) => {
  /**
   * Split on first occurrence
   * Example: [workflow_tags.Parse.ly Internal Agent]
   * Result: key = workflow_tags, value = Parse.ly Internal Agent
   */
  const index = filterString.indexOf('.');
  // Extract the part before the first '.'
  const key = index !== -1 ? filterString.slice(0, index) : filterString;
  // Extract the part after the first '.'
  const value = index !== -1 ? filterString.slice(index + 1) : '';

  return { key, value };
};

/**
 * - Compact notation only if `number > 9999`
 * - Lowercase affixes, e. g. `k`, `m`
 * - 1 fraction digit for compact format, e. g. `10.1k`
 * - If `number <= 9999`, use "string with commas" format, e. g. `1,234.56`
 */
export function numberToCompactNotation(number: number) {
  return number > 9999
    ? Intl.NumberFormat('en', {
        maximumFractionDigits: 1,
        notation: 'compact',
      })
        .format(number)
        .toLowerCase()
    : numbersToStringWithCommas({
        canBeFloat: true,
        number: number,
      });
}

export const colorMap = (palette: Palette): ColorMapConfig => ({
  csat: {
    1: palette.colors.primary[200],
    2: palette.colors.primary[300],
    3: palette.colors.primary[400],
    4: palette.colors.primary[500],
    5: palette.colors.primary[600],
    bad: palette.colors.primary[300],
    excellent: palette.colors.primary[600],
    good: palette.colors.primary[500],
    ok: palette.colors.primary[400],
    terrible: palette.colors.primary[200],
  },
  deflection: {
    deflected: palette.colors.teal[500],
    mixed: palette.colors.blue[400],
    negative: palette.colors.red[500],
    not_deflected: palette.colors.red[500],
    positive: palette.colors.blue[700],
  },
  eligible: {
    eligible: palette.colors.teal[500],
    ineligible: palette.colors.red[500],
  },
  empty: palette.colors.slate[100],
  engagement: {
    engaged: palette.colors.blue[700], // Use for no-dropoff
    not_engaged: palette.colors.red[500], // Use for dropoff
    somewhat_engaged: palette.colors.blue[400],
  },
  feedback: {
    negative: palette.colors.red[500],
    neutral: palette.colors.green[300],
    positive: palette.colors.green[500],
    unanswered: palette.colors.slate[100],
  },
  general: palette.colors.primary[500],
  na: palette.colors.grey[500],
  relevance: {
    irrelevant: palette.colors.red[500],
    relevant: palette.colors.blue[700],
    somewhat_relevant: palette.colors.blue[400],
  },
  savings: {
    potential: palette.colors.red[500],
    potential_ghost: palette.colors.red[100],
    realized: palette.colors.teal[500],
    realized_ghost: palette.colors.teal[100],
  },
  sentiment: {
    negative: palette.colors.red[500],
    neutral: palette.colors.green[300],
    positive: palette.colors.green[500],
  },
  voice: {
    inbound: palette.chart[4],
    outbound: palette.chart[13],
  },
});

export const countLeafNodes = (topics: InsightTopicData[]): number => {
  let count = 0;
  for (const topic of topics) {
    if (topic.insight_subtopics.length === 0) {
      count += 1;
    } else {
      count += countLeafNodes(topic.insight_subtopics);
    }
  }
  return count;
};

/**
 * Voice sentiment filter has a different name in the backend API, so we need to
 * rename `ending_sentiments` to `voice_sentiment` when the channel is `voice`.
 */
export function normalizeFilters<T extends Record<string, unknown>>(
  filters: T,
) {
  const { ending_sentiments, ...rest } = filters;

  if (!ending_sentiments || typeof ending_sentiments !== 'string') {
    return filters;
  }

  return rest.channels === 'voice'
    ? {
        ...rest,
        voice_sentiment: ending_sentiments.replace(
          'ending_sentiments',
          'voice_sentiment',
        ),
      }
    : {
        ...rest,
        ending_sentiments,
      };
}

/**
 * Sorts scores by value descending with n/a (null) scores placed last.
 * Used by AI QA insight cards (LabelsCard, ScoreRangeCard).
 */
export function sortScoresWithNaLast<T extends { score: number | null }>(
  scores: T[],
): T[] {
  return [...scores].sort((a, b) => {
    if (a.score === null && b.score === null) {
      return 0;
    }
    if (a.score === null) {
      return 1;
    }
    if (b.score === null) {
      return -1;
    }
    return b.score - a.score;
  });
}
